import {
  Component,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core'
import { MatCardModule } from '@angular/material/card'
import { MatIconModule } from '@angular/material/icon'
import { ProductPlasticsDeclarationResponse } from '../../../types/products'
import { SpinnerService } from '../../../services/spinner.service'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import { Dialog } from '@angular/cdk/dialog'
import { switchMap, catchError, of, map } from 'rxjs'
import { DialogService } from '../../../services/dialog.service'
import { ErrorService } from '../../../services/error.service'
import { ProductService } from '../../../services/product.service'
import { RouteService } from '../../../services/route.service'
import { TranslationService } from '../../../services/translation.service'
import { WindowService } from '../../../services/window.service'
import { ApiError } from '../../../types/apiError'
import { ROUTES } from '../../../constants/routes'
import { DatePipe, TitleCasePipe } from '@angular/common'
import { MatTooltip } from '@angular/material/tooltip'
import {
  PRODUCT_MATERIAL_TYPE_RAW_RESIN,
  PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT,
  PRODUCT_MATERIAL_TYPE_NON_PLASTIC,
  PRODUCT_MATERIAL_TYPE_UNDECLARED,
} from '../../../constants/plastics-declaration'
import { MatDivider } from '@angular/material/divider'
import { MatChip, MatChipSet } from '@angular/material/chips'

const MATERIAL_TYPE_LABELS: Record<string, string> = {
  [PRODUCT_MATERIAL_TYPE_RAW_RESIN]: 'Raw Resin',
  [PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT]: 'Plastic Product',
  [PRODUCT_MATERIAL_TYPE_NON_PLASTIC]: 'Non-plastic',
  [PRODUCT_MATERIAL_TYPE_UNDECLARED]: 'Composition not declared',
}

@Component({
  selector: 'app-product-plastics-declaration',
  imports: [
    MatIconModule,
    MatCardModule,
    TitleCasePipe,
    MatTooltip,
    DatePipe,
    MatDivider,
    MatChipSet,
    MatChip,
  ],
  templateUrl: './product-plastics-declaration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-plastics-declaration.component.scss',
})
export class ProductPlasticsDeclarationComponent implements OnInit {
  declaration?: ProductPlasticsDeclarationResponse

  route = inject(ActivatedRoute)
  router = inject(Router)
  dialog = inject(Dialog)
  dialogService = inject(DialogService)
  routeService = inject(RouteService)
  productService = inject(ProductService)
  spinnerService = inject(SpinnerService)
  errorService = inject(ErrorService)
  windowService = inject(WindowService)
  translationService = inject(TranslationService)

  async ngOnInit() {
    this.spinnerService.startSpinner()
    this.route
      .parent!.paramMap.pipe(
        switchMap((params: ParamMap) => {
          const id = params.get('id')
          if (id != null) {
            return this.productService.getProductPlasticsDeclaration(id).pipe(
              catchError((err) => {
                this.spinnerService.stopSpinner()
                const apiErr = err as ApiError
                this.errorService.handleErr(
                  this.dialogService,
                  apiErr,
                  this.dialog,
                )
                return of(null)
              }),
            )
          } else {
            return of(null)
          }
        }),
        map((plasticsDeclaration) => {
          if (plasticsDeclaration != null) {
            this.declaration = plasticsDeclaration
            const currentRoute = this.routeService.getProductViewRoutes(
              this.declaration.name,
              ROUTES.PRODUCT_PLASTICS_DECLARATION.path,
              this.declaration.id,
            )
            this.productService.currentProductData.set({
              id: this.declaration.id,
              name: this.declaration.name,
              brand: this.declaration.brand,
            })

            this.routeService.setRoute(currentRoute)
          }
        }),
      )
      .subscribe({
        next: () => {
          this.spinnerService.stopSpinner()
        },
        error: (err) => {
          this.spinnerService.stopSpinner()
          const apiErr = err as ApiError
          this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
        },
      })
  }

  // Returns true if the calculation method equals "Other" (case-insensitive).
  isCalculationMethodOther(method: string | null): boolean {
    const OTHER_METHOD = 'Other'
    return method?.toLowerCase() === OTHER_METHOD.toLowerCase()
  }

  // Returns a human-readable label for the material type (e.g., "RawResin" -> "Raw Resin").
  materialTypeLabel(mt: string | null | undefined): string {
    if (!mt) return this.translationService.getNaText()
    return MATERIAL_TYPE_LABELS[mt] ?? mt
  }

  // True when the current declaration is a Plastic Product.
  isPlasticProduct(): boolean {
    const t = this.declaration?.productMaterialType
    return t === PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT
  }

  // True when the current declaration is a Raw Resin.
  isRawResin(): boolean {
    const t = this.declaration?.productMaterialType
    return t === PRODUCT_MATERIAL_TYPE_RAW_RESIN
  }

  getWasteStreamText(
    isResidential: boolean | null | undefined,
    isIci: boolean | null | undefined,
    isCdr: boolean | null | undefined,
  ): string {
    const selectedStreams: string[] = []

    if (isResidential)
      selectedStreams.push(this.translationService.getResidentialText())
    if (isIci) selectedStreams.push(this.translationService.getIciText())
    if (isCdr) selectedStreams.push(this.translationService.getCdrText())

    return selectedStreams.length > 0
      ? selectedStreams.join(', ')
      : this.translationService.getNaText()
  }
}
